import type { NextRouter } from "next/router";

type SafePushOptions = {
  timeoutMs?: number;
  fallback?: "assign" | "replace";
};

export const safePush = (router: NextRouter, url: string, options: SafePushOptions = {}) => {
  const timeoutMs = options.timeoutMs ?? 800;
  const fallback = options.fallback ?? "assign";

  if (typeof window === "undefined") {
    return router.push(url);
  }

  return new Promise<boolean>((resolve) => {
    let timeoutId: number | undefined;
    let done = false;

    const cleanup = () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
      router.events.off("routeChangeComplete", onComplete);
      router.events.off("routeChangeError", onError);
    };

    const finish = (ok: boolean) => {
      if (done) return;
      done = true;
      cleanup();
      resolve(ok);
    };

    const doFallback = () => {
      finish(false);
      if (fallback === "replace") {
        window.location.replace(url);
      } else {
        window.location.assign(url);
      }
    };

    const onComplete = (to: string) => {
      if (to === url || to.startsWith(`${url}?`) || to.startsWith(`${url}#`)) {
        finish(true);
      }
    };

    const onError = () => {
      doFallback();
    };

    router.events.on("routeChangeComplete", onComplete);
    router.events.on("routeChangeError", onError);

    timeoutId = window.setTimeout(doFallback, timeoutMs);
    router.push(url).catch(doFallback);
  });
};

